import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Activity, Search, Filter, User, Calendar, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

const ACTION_COLORS = {
    LOGIN: 'bg-blue-100 text-blue-700',
    CREATE: 'bg-green-100 text-green-700',
    UPDATE: 'bg-amber-100 text-amber-700',
    DELETE: 'bg-red-100 text-red-700',
    BACKUP: 'bg-purple-100 text-purple-700',
    RESTORE: 'bg-orange-100 text-orange-700',
    APPROVE: 'bg-teal-100 text-teal-700',
    REJECT: 'bg-slate-100 text-slate-700',
};
const RESOURCE_ICONS = { citizens: '👥', iuran: '💰', expenses: '📋', users: '👤', auth: '🔐', settings: '⚙️', announcements: '📢', complaints: '📝', meetings: '📅', guests: '🏠', iuran_multi: '💰' };
const ACTIONS = ['', 'LOGIN', 'CREATE', 'UPDATE', 'DELETE', 'BACKUP', 'RESTORE', 'APPROVE', 'REJECT'];

export default function ActivityLogsPage() {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [username, setUsername] = useState('');
    const [action, setAction] = useState('');
    const LIMIT = 50;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: LIMIT });
            if (username) params.set('username', username);
            if (action) params.set('action', action);
            const res = await axios.get(`${API}/activity-logs?${params}`, { withCredentials: true });
            setLogs(res.data.data || []);
            setTotal(res.data.total || 0);
        } catch (e) {
            if (e.response?.status === 403) toast.error('Hanya admin yang bisa melihat log aktivitas');
            else toast.error('Gagal memuat log');
        }
        setLoading(false);
    }, [page, username, action]);

    useEffect(() => { load(); }, [load]);

    const totalPages = Math.ceil(total / LIMIT);

    return (
        <div className="space-y-5" data-testid="activity-logs-page">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="page-header text-2xl flex items-center gap-3">
                        <Activity className="w-6 h-6 text-primary" /> Log Aktivitas Sistem
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">{total} aktivitas tercatat</p>
                </div>
                <Button variant="outline" onClick={load} className="gap-2 h-9" data-testid="refresh-logs-btn">
                    <RefreshCw className="w-4 h-4" /> Refresh
                </Button>
            </div>

            {/* Filters */}
            <div className="flex gap-3 flex-wrap bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 flex-1 min-w-40">
                    <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <Input placeholder="Filter username..." value={username}
                        onChange={e => { setUsername(e.target.value); setPage(1); }}
                        className="h-8 text-sm border-0 p-0 focus:ring-0 bg-transparent" data-testid="filter-username" />
                </div>
                <Select value={action} onValueChange={v => { setAction(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="w-36 h-8 text-sm" data-testid="filter-action"><SelectValue placeholder="Semua aksi" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Semua Aksi</SelectItem>
                        {ACTIONS.slice(1).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50">
                                <TableHead className="font-semibold text-slate-700">Waktu</TableHead>
                                <TableHead className="font-semibold text-slate-700">Username</TableHead>
                                <TableHead className="font-semibold text-slate-700">Aksi</TableHead>
                                <TableHead className="font-semibold text-slate-700 hidden md:table-cell">Modul</TableHead>
                                <TableHead className="font-semibold text-slate-700">Keterangan</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? [...Array(8)].map((_, i) => (
                                <TableRow key={i}>{[...Array(5)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                            )) : logs.length === 0 ? (
                                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                                    <Activity className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                                    Belum ada aktivitas tercatat
                                </TableCell></TableRow>
                            ) : logs.map(log => (
                                <TableRow key={log.id} className="hover:bg-slate-50 text-sm">
                                    <TableCell className="text-slate-500 text-xs whitespace-nowrap">
                                        <p>{new Date(log.timestamp).toLocaleDateString('id-ID')}</p>
                                        <p className="text-slate-400">{new Date(log.timestamp).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                                <span className="text-xs font-bold text-primary">{log.username?.[0]?.toUpperCase() || '?'}</span>
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-800">{log.username}</p>
                                                <p className="text-xs text-slate-400">{log.role}</p>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className={`text-xs px-2 py-1 rounded-full font-bold ${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-600'}`}>
                                            {log.action}
                                        </span>
                                    </TableCell>
                                    <TableCell className="hidden md:table-cell text-slate-500">
                                        <span className="flex items-center gap-1.5">
                                            <span>{RESOURCE_ICONS[log.resource] || '📦'}</span>
                                            <span className="capitalize">{log.resource}</span>
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-slate-600 max-w-xs">
                                        <p className="truncate text-xs">{log.details}</p>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                        <p className="text-xs text-slate-500">
                            Hal. {page} dari {totalPages} ({total} total)
                        </p>
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setPage(p => p - 1)} disabled={page === 1} className="h-8 text-xs">
                                ← Sebelumnya
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="h-8 text-xs">
                                Berikutnya →
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
